// A line's details (spec §3.9), opened from its row's Details (the Worklist, History) or from the
// System log's Go to line — and back to the log by Open in System log: its fields, its
// pickup or shipment, what A2000 shipped, its source and pick tickets (a Deposco line: its SOs and
// their statuses), and its history.

import type { ReactNode } from "react";
import { ScrollTextIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import {
  PAGES,
  REVIEW_LABELS,
  valuesText,
  a2000Lines,
  a2000Text,
  formatDay,
  lineRef,
  poPtText,
  ptChangeText,
  timeShown,
} from "@app/shared";
import type { LineView, RoutingWindow } from "@app/shared";
import { Button, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@axny/platform-ui";
import { LineHistory } from "./line-history";
import { DivisionText, NamedText, customerItems } from "./named";
import { pickupText } from "./shipping-writer";
import { LineSourceFacts } from "./source-filter";

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="font-medium break-words">{children || "—"}</p>
    </div>
  );
}

const day = (d: string | null | undefined) => (d && /^\d{4}-/.test(d) ? formatDay(d) : (d ?? ""));

/**
 * A finished line's review, as History no longer shows it in a column: who approved it and when,
 * as the line saved it. A workbook row was never reviewed in the app: nothing.
 */
function reviewText(line: LineView): string | null {
  if (line.excel) return null;
  const s = line.stored;
  if (s?.reviewStatus !== "Approved") return "Never approved";
  const on = s.reviewedAt ? ` on ${day(s.reviewedAt.slice(0, 10))}` : "";
  return s.reviewedBy ? `Approved by ${s.reviewedBy}${on}` : `Approved${on}`;
}

export function LineDetails({
  line,
  routingWindow,
  onClose,
}: {
  line: LineView;
  /** An open line's routing window: its whole sentence (the grid's cell says it short). */
  routingWindow?: RoutingWindow;
  onClose: () => void;
}) {
  const f = line.fields;
  const navigate = useNavigate();
  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-xl" data-testid="line-details">
        <SheetHeader>
          <SheetTitle>{line.customerNames.join(", ") || "Line"}</SheetTitle>
          <SheetDescription>
            {poPtText(line, 3)} · {lineRef(line) || "no WMS #"} ·{" "}
            {line.finished ? `${line.finished.status} ${day(line.finished.at)}` : REVIEW_LABELS[line.review]}
          </SheetDescription>
        </SheetHeader>
        <div className="space-y-5 px-4 pb-6 text-sm">
          <LineSourceFacts line={line} />
          <section className="grid grid-cols-3 gap-3">
            <Fact label="Received">{day(line.ptDate)}</Fact>
            <Fact label="First ship">{day(line.start)}</Fact>
            <Fact label="Cancel">{day(line.cancel)}</Fact>
            {routingWindow && !line.finished ? (
              <Fact label="Routing window">
                {routingWindow.label}
                {routingWindow.routeBy ? ` · route by ${day(routingWindow.routeBy)}` : ""}
              </Fact>
            ) : null}
            <Fact label="QTY">{line.qty.toLocaleString("en-US")}</Fact>
            <Fact label="Warehouse">{line.whs.join(", ")}</Fact>
            <Fact label="Customer">
              {line.customers.length ? <NamedText items={customerItems(line)} className="" /> : null}
            </Fact>
            <Fact label="Division">{line.divs.length ? <DivisionText divs={line.divs} className="" /> : null}</Fact>
            <Fact label="READY DATE">{day(f.readyDate)}</Fact>
            <Fact label="Routing status">{f.routingStatus}</Fact>
            <Fact label="Routing Reference">{f.routingRef}</Fact>
            <Fact label="SCHEDULED SHIP DATE">{day(f.schedShip)}</Fact>
            <Fact label="Work Needed">{valuesText(f.workNeeded)}</Fact>
            <Fact label="Production Notes">{f.productionNotes}</Fact>
            <Fact label="Routing Notes">{f.routingNotes}</Fact>
          </section>
          {line.finished && reviewText(line) ? <p className="text-muted-foreground">{reviewText(line)}</p> : null}
          {line.onPickup ? (
            <section className="rounded-lg border border-teal-200 bg-teal-50/60 p-3">
              <p className="font-medium">On the pickup {pickupText(line.onPickup.pickup)}</p>
              <p className="text-muted-foreground text-xs">
                {line.onPickup.pickup.appTime ? `App Time ${timeShown(line.onPickup.pickup.appTime)} · ` : ""}
                {line.onPickup.pickup.door ? `Door ${line.onPickup.pickup.door}` : "no door yet"}
              </p>
            </section>
          ) : null}
          {line.shipment ? (
            <section className="rounded-lg border border-teal-200 bg-teal-50/60 p-3">
              <p className="font-medium">
                Shipped {day(line.shipment.shipDate)} ·{" "}
                {[line.shipment.carrier, line.shipment.loadRef].filter(Boolean).join(" · ")}
                {line.finished?.by ? ` · by ${line.finished.by}` : ""}
              </p>
              {line.shipment.tracking ? (
                <p className="text-muted-foreground text-xs">Tracking {line.shipment.tracking}</p>
              ) : null}
            </section>
          ) : null}
          {line.a2000 ? (
            <section>
              <p className="font-medium">{a2000Text(line.a2000)}</p>
              <ul className="text-muted-foreground mt-1 space-y-0.5 text-xs">
                {a2000Lines(line.a2000).map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </ul>
            </section>
          ) : null}
          {line.source === "A2000" ? (
            <section>
              <h3 className="text-muted-foreground mb-1 text-xs font-semibold tracking-wider uppercase">
                Pick tickets
              </h3>
              <p>{line.pts.map((p) => `${p.pickTicket}${p.gone ? " (no longer open in A2000)" : ""}`).join(", ")}</p>
            </section>
          ) : null}
          {line.ptChanges.length ? (
            <section data-testid="pt-changes">
              <h3 className="text-muted-foreground mb-1 text-xs font-semibold tracking-wider uppercase">
                Changed in {line.source} since approval
              </h3>
              <ul className="space-y-0.5">
                {line.ptChanges.map((x) => (
                  <li key={`${x.pickTicket}|${x.field}`}>{ptChangeText(x)}</li>
                ))}
              </ul>
            </section>
          ) : null}
          {line.stored ? (
            <>
              <Button
                variant="outline"
                size="sm"
                className="gap-1"
                // The pages are code routes (`pages.tsx`): their paths and search are not in the router's types.
                onClick={() =>
                  void navigate({ to: PAGES["system-log"].path as never, search: { line: line.stored!.id } as never })
                }
                data-testid="details-open-log"
              >
                <ScrollTextIcon className="size-3.5" aria-hidden />
                Open in System log
              </Button>
              <LineHistory lineId={line.stored.id} />
            </>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
